/**
 * StudentsPage Component
 * Display and manage list of students referred by the teacher
 */

import React, { useEffect, useState } from 'react';
import { useTeachStore } from '../../stores/teachStore';
import { Search, Loader2, ChevronDown } from 'lucide-react';
import { StudentList } from '../../components/teach/StudentList';
import { StudentDetail } from '../../components/teach/StudentDetail';
import Pagination from '../../components/common/Pagination';
import { MessageDialog } from '../../components/common/MessageDialog';

export const StudentsPage: React.FC = () => {
  const {
    students,
    studentsPagination,
    studentsLoading,
    classes,
    fetchStudents,
    fetchClasses,
    fetchStudentDetail,
    studentDetail,
    clearStudentDetail,
  } = useTeachStore();

  const [activeTab, setActiveTab] = useState<string>('all');
  const [membershipFilter, setMembershipFilter] = useState<string>('all');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Message dialog state
  const [messageDialog, setMessageDialog] = useState<{
    isOpen: boolean;
    title?: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showMessage = (message: string, type: 'success' | 'error' | 'info' = 'info', title?: string) => {
    setMessageDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeMessageDialog = () => {
    setMessageDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Grade options
  const gradeOptions = [
    { value: 'all', label: '全部年级' },
    { value: '小学一年级', label: '小学一年级' },
    { value: '小学二年级', label: '小学二年级' },
    { value: '小学三年级', label: '小学三年级' },
    { value: '小学四年级', label: '小学四年级' },
    { value: '小学五年级', label: '小学五年级' },
    { value: '小学六年级', label: '小学六年级' },
    { value: '初中一年级', label: '初中一年级' },
    { value: '初中二年级', label: '初中二年级' },
    { value: '初中三年级', label: '初中三年级' },
    { value: '高中一年级', label: '高中一年级' },
    { value: '高中二年级', label: '高中二年级' },
    { value: '高中三年级', label: '高中三年级' },
  ];

  // Load classes on mount
  useEffect(() => {
    fetchClasses().catch(err => {
      showMessage('加载班级列表失败', 'error');
    });
  }, []);

  // Load students when filters change
  useEffect(() => {
    const filters = {
      classId: activeTab !== 'all' ? activeTab : undefined,
      membershipType: membershipFilter !== 'all' ? membershipFilter : undefined,
      grade: gradeFilter !== 'all' ? gradeFilter : undefined,
      search: searchQuery || undefined,
      page: currentPage,
      pageSize: 20,
    };

    fetchStudents(filters).catch(err => {
      showMessage('加载学生列表失败', 'error');
    });
  }, [activeTab, membershipFilter, gradeFilter, searchQuery, currentPage]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleViewDetail = async (studentId: string) => {
    try {
      await fetchStudentDetail(studentId);
      setShowDetailModal(true);
    } catch (error) {
      showMessage('加载学生详情失败', 'error');
    }
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    clearStudentDetail();
  };

  const handleAddToClass = async (studentId: string, classId: string) => {
    // This will be implemented in MemberManager
    showMessage('请在班级管理页面添加学生到班级', 'info');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="p-6">
      {/* Top tabs for class filter */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg w-fit min-w-full">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'all'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            全部
          </button>
          {classes.map(cls => (
            <button
              key={cls.id}
              onClick={() => handleTabChange(cls.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === cls.id
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {cls.name}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Membership filter */}
        <div className="relative">
          <select
            value={membershipFilter}
            onChange={(e) => {
              setMembershipFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="appearance-none w-full sm:w-48 px-4 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">全部会员状态</option>
            <option value="creditPay">按次付费</option>
            <option value="monthly">月会员</option>
            <option value="yearly">年会员</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Grade filter */}
        <div className="relative">
          <select
            value={gradeFilter}
            onChange={(e) => {
              setGradeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="appearance-none w-full sm:w-48 px-4 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {gradeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="搜索昵称或手机号"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Student list */}
      {studentsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">暂无学生数据</p>
        </div>
      ) : (
        <>
          <StudentList
            students={students}
            classes={classes}
            onViewDetail={handleViewDetail}
            onAddToClass={handleAddToClass}
          />

          {/* Pagination */}
          {studentsPagination && studentsPagination.totalPages > 1 && (
            <Pagination
              currentPage={studentsPagination.page}
              totalPages={studentsPagination.totalPages}
              totalItems={studentsPagination.total}
              itemsPerPage={studentsPagination.pageSize}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Student detail modal */}
      {showDetailModal && studentDetail && (
        <StudentDetail
          student={studentDetail}
          onClose={handleCloseDetail}
        />
      )}

      {/* Message dialog */}
      <MessageDialog
        isOpen={messageDialog.isOpen}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
        onClose={closeMessageDialog}
      />
    </div>
  );
};
